import React, { useCallback, useState } from "react";
import { Button } from "../../../../design/button";
import { Input } from "../../../../design/input";
import { Select } from "../../../../design/select";
import { Textarea } from "../../../../design/textarea";
import { Checkbox, CheckboxField } from "../../../../design/checkbox";
import {
  Field,
  Fieldset,
  FieldGroup,
  Label,
  Description,
} from "../../../../design/fieldset";
import { Divider } from "../../../../design/divider";
import {
  PlusIcon,
  TrashIcon,
  UserIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "@heroicons/react/20/solid";
import {
  WizardFormData,
  ParentFormData,
  RELATIONSHIP_OPTIONS,
  CUSTODY_STATUS_OPTIONS,
  GENDER_OPTIONS,
  MARITAL_STATUS_OPTIONS,
  LANGUAGE_OPTIONS,
  INCOME_LEVEL_OPTIONS,
  EDUCATION_LEVEL_OPTIONS,
  createEmptyParent,
} from "./types";
import clsx from "clsx";

interface ParentsStepProps {
  formData: WizardFormData;
  setFormData: React.Dispatch<React.SetStateAction<WizardFormData>>;
  onNext: () => void;
  onBack: () => void;
}

const ParentsStep: React.FC<ParentsStepProps> = ({
  formData,
  setFormData,
  onNext,
  onBack,
}) => {
  const [expandedParents, setExpandedParents] = useState<Record<string, boolean>>({});

  const toggleExpanded = (parentId: string) => {
    setExpandedParents((prev) => ({
      ...prev,
      [parentId]: !prev[parentId],
    }));
  };

  const handleParentChange = useCallback(
    (index: number, field: keyof ParentFormData) =>
      (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        setFormData((prev) => {
          const newParents = [...prev.parents];
          newParents[index] = {
            ...newParents[index],
            [field]: e.target.value,
          };
          return { ...prev, parents: newParents };
        });
      },
    [setFormData]
  );

  const handleParentCheckboxChange = useCallback(
    (index: number, field: keyof ParentFormData) => (checked: boolean) => {
      setFormData((prev) => {
        const newParents = [...prev.parents];
        newParents[index] = {
          ...newParents[index],
          [field]: checked,
        };
        return { ...prev, parents: newParents };
      });
    },
    [setFormData]
  );

  const handleAddParent = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      parents: [...prev.parents, createEmptyParent()],
    }));
  }, [setFormData]);

  const handleRemoveParent = useCallback(
    (index: number) => {
      setFormData((prev) => ({
        ...prev,
        parents: prev.parents.filter((_, i) => i !== index),
      }));
    },
    [setFormData]
  );

  // At least one parent with first and last name is required
  const isValid = formData.parents.some(
    (p) => p.firstName.trim() && p.lastName.trim()
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">
            Parents / Guardians
          </h3>
          <p className="text-sm text-zinc-500 dark:text-zinc-400">
            Add all parents or guardians involved in this case
          </p>
        </div>
        <Button type="button" outline onClick={handleAddParent}>
          <PlusIcon className="h-4 w-4" />
          Add Parent
        </Button>
      </div>

      {formData.parents.map((parent, index) => (
        <div
          key={parent.id}
          className="rounded-lg border border-zinc-200 dark:border-zinc-700 p-4"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900">
                <UserIcon className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              </div>
              <span className="font-medium text-zinc-900 dark:text-zinc-100">
                Parent {index + 1}
              </span>
            </div>
            {formData.parents.length > 1 && (
              <Button
                type="button"
                plain
                onClick={() => handleRemoveParent(index)}
                className="text-red-600 hover:text-red-700"
              >
                <TrashIcon className="h-4 w-4" />
                Remove
              </Button>
            )}
          </div>

          <Fieldset>
            <FieldGroup>
              {/* Name fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field>
                  <Label htmlFor={`parent-${index}-firstName`}>
                    First Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id={`parent-${index}-firstName`}
                    type="text"
                    placeholder="First name"
                    value={parent.firstName}
                    onChange={handleParentChange(index, "firstName")}
                  />
                </Field>

                <Field>
                  <Label htmlFor={`parent-${index}-lastName`}>
                    Last Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id={`parent-${index}-lastName`}
                    type="text"
                    placeholder="Last name"
                    value={parent.lastName}
                    onChange={handleParentChange(index, "lastName")}
                  />
                </Field>
              </div>

              {/* Contact fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field>
                  <Label htmlFor={`parent-${index}-email`}>Email</Label>
                  <Input
                    id={`parent-${index}-email`}
                    type="email"
                    placeholder="email@example.com"
                    value={parent.email}
                    onChange={handleParentChange(index, "email")}
                  />
                </Field>

                <Field>
                  <Label htmlFor={`parent-${index}-phoneNumber`}>
                    Best Phone Number
                  </Label>
                  <Input
                    id={`parent-${index}-phoneNumber`}
                    type="tel"
                    placeholder="(555) 555-5555"
                    value={parent.phoneNumber}
                    onChange={handleParentChange(index, "phoneNumber")}
                  />
                </Field>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field>
                  <Label htmlFor={`parent-${index}-alternatePhone`}>
                    Alternate Phone
                  </Label>
                  <Input
                    id={`parent-${index}-alternatePhone`}
                    type="tel"
                    placeholder="(555) 555-5555"
                    value={parent.alternatePhone}
                    onChange={handleParentChange(index, "alternatePhone")}
                  />
                </Field>

                <Field>
                  <Label htmlFor={`parent-${index}-dateOfBirth`}>
                    Date of Birth
                  </Label>
                  <Input
                    id={`parent-${index}-dateOfBirth`}
                    type="date"
                    value={parent.dateOfBirth}
                    onChange={handleParentChange(index, "dateOfBirth")}
                  />
                </Field>
              </div>

              {/* Relationship and custody */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field>
                  <Label htmlFor={`parent-${index}-relationship`}>
                    Relationship to Child
                  </Label>
                  <Select
                    id={`parent-${index}-relationship`}
                    value={parent.relationshipToChild}
                    onChange={handleParentChange(index, "relationshipToChild")}
                  >
                    <option value="">Select relationship...</option>
                    {RELATIONSHIP_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </Field>

                <Field>
                  <Label htmlFor={`parent-${index}-custodyStatus`}>
                    Custody Status
                  </Label>
                  <Select
                    id={`parent-${index}-custodyStatus`}
                    value={parent.custodyStatus}
                    onChange={handleParentChange(index, "custodyStatus")}
                  >
                    <option value="">Select status...</option>
                    {CUSTODY_STATUS_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </Field>
              </div>

              {/* Role checkboxes */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <CheckboxField>
                  <Checkbox
                    checked={parent.isVisitingParent}
                    onChange={handleParentCheckboxChange(index, "isVisitingParent")}
                  />
                  <Label>Visiting Parent</Label>
                </CheckboxField>

                <CheckboxField>
                  <Checkbox
                    checked={parent.isCustodialParent}
                    onChange={handleParentCheckboxChange(index, "isCustodialParent")}
                  />
                  <Label>Custodial Parent</Label>
                </CheckboxField>
              </div>

              <Divider soft />

              {/* Address */}
              <Field>
                <Label htmlFor={`parent-${index}-streetAddress`}>
                  Street Address
                </Label>
                <Input
                  id={`parent-${index}-streetAddress`}
                  type="text"
                  placeholder="123 Main Street"
                  value={parent.streetAddress}
                  onChange={handleParentChange(index, "streetAddress")}
                />
              </Field>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Field className="col-span-2 md:col-span-1">
                  <Label htmlFor={`parent-${index}-city`}>City</Label>
                  <Input
                    id={`parent-${index}-city`}
                    type="text"
                    placeholder="City"
                    value={parent.city}
                    onChange={handleParentChange(index, "city")}
                  />
                </Field>

                <Field>
                  <Label htmlFor={`parent-${index}-state`}>State</Label>
                  <Input
                    id={`parent-${index}-state`}
                    type="text"
                    placeholder="NC"
                    value={parent.state}
                    onChange={handleParentChange(index, "state")}
                    maxLength={2}
                  />
                </Field>

                <Field>
                  <Label htmlFor={`parent-${index}-zipCode`}>ZIP Code</Label>
                  <Input
                    id={`parent-${index}-zipCode`}
                    type="text"
                    placeholder="28144"
                    value={parent.zipCode}
                    onChange={handleParentChange(index, "zipCode")}
                  />
                </Field>
              </div>

              {/* Collapsible Additional Information */}
              <div className="border-t border-zinc-200 dark:border-zinc-700 pt-4 mt-4">
                <button
                  type="button"
                  onClick={() => toggleExpanded(parent.id)}
                  className="flex items-center justify-between w-full text-left text-sm font-medium text-zinc-700 dark:text-zinc-300 hover:text-zinc-900 dark:hover:text-zinc-100"
                >
                  <span>Additional Information (Demographics, Health, Emergency Contact)</span>
                  {expandedParents[parent.id] ? (
                    <ChevronUpIcon className="h-5 w-5" />
                  ) : (
                    <ChevronDownIcon className="h-5 w-5" />
                  )}
                </button>

                {expandedParents[parent.id] && (
                  <div className="mt-4 space-y-4">
                    {/* Demographics */}
                    <div>
                      <h4 className="text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-3">
                        Demographics
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Field>
                          <Label htmlFor={`parent-${index}-sex`}>Sex</Label>
                          <Select
                            id={`parent-${index}-sex`}
                            value={parent.sex}
                            onChange={handleParentChange(index, "sex")}
                          >
                            <option value="">Select...</option>
                            {GENDER_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </Select>
                        </Field>

                        <Field>
                          <Label htmlFor={`parent-${index}-race`}>Race</Label>
                          <Input
                            id={`parent-${index}-race`}
                            type="text"
                            placeholder="Race/Ethnicity"
                            value={parent.race}
                            onChange={handleParentChange(index, "race")}
                          />
                        </Field>

                        <Field>
                          <Label htmlFor={`parent-${index}-primaryLanguage`}>Primary Language</Label>
                          <Select
                            id={`parent-${index}-primaryLanguage`}
                            value={parent.primaryLanguage}
                            onChange={handleParentChange(index, "primaryLanguage")}
                          >
                            <option value="">Select...</option>
                            {LANGUAGE_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </Select>
                        </Field>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                        <Field>
                          <Label htmlFor={`parent-${index}-maritalStatus`}>Marital Status</Label>
                          <Select
                            id={`parent-${index}-maritalStatus`}
                            value={parent.maritalStatus}
                            onChange={handleParentChange(index, "maritalStatus")}
                          >
                            <option value="">Select...</option>
                            {MARITAL_STATUS_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </Select>
                        </Field>

                        <Field>
                          <Label htmlFor={`parent-${index}-incomeLevel`}>Income Level</Label>
                          <Select
                            id={`parent-${index}-incomeLevel`}
                            value={parent.incomeLevel}
                            onChange={handleParentChange(index, "incomeLevel")}
                          >
                            <option value="">Select...</option>
                            {INCOME_LEVEL_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </Select>
                        </Field>

                        <Field>
                          <Label htmlFor={`parent-${index}-educationLevel`}>Education Level</Label>
                          <Select
                            id={`parent-${index}-educationLevel`}
                            value={parent.educationLevel}
                            onChange={handleParentChange(index, "educationLevel")}
                          >
                            <option value="">Select...</option>
                            {EDUCATION_LEVEL_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </Select>
                        </Field>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                        <CheckboxField>
                          <Checkbox
                            checked={parent.translatorNeeded}
                            onChange={handleParentCheckboxChange(index, "translatorNeeded")}
                          />
                          <Label>Translator Needed</Label>
                        </CheckboxField>

                        <CheckboxField>
                          <Checkbox
                            checked={parent.isRuralArea}
                            onChange={handleParentCheckboxChange(index, "isRuralArea")}
                          />
                          <Label>Rural Area</Label>
                        </CheckboxField>
                      </div>
                    </div>

                    <Divider soft />

                    {/* Health Information */}
                    <div>
                      <h4 className="text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-3">
                        Health Information
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Field>
                          <Label htmlFor={`parent-${index}-medicalProblems`}>
                            Medical Problems / Disabilities
                          </Label>
                          <Textarea
                            id={`parent-${index}-medicalProblems`}
                            placeholder="Any medical problems or disabilities staff should be aware of"
                            value={parent.medicalProblems}
                            onChange={handleParentChange(index, "medicalProblems")}
                            rows={2}
                          />
                        </Field>

                        <Field>
                          <Label htmlFor={`parent-${index}-allergies`}>Allergies</Label>
                          <Textarea
                            id={`parent-${index}-allergies`}
                            placeholder="Any known allergies"
                            value={parent.allergies}
                            onChange={handleParentChange(index, "allergies")}
                            rows={2}
                          />
                        </Field>
                      </div>
                    </div>

                    <Divider soft />

                    {/* Emergency Contact */}
                    <div>
                      <h4 className="text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-3">
                        Emergency Contact
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Field>
                          <Label htmlFor={`parent-${index}-emergencyContactName`}>Name</Label>
                          <Input
                            id={`parent-${index}-emergencyContactName`}
                            type="text"
                            placeholder="Emergency contact name"
                            value={parent.emergencyContactName}
                            onChange={handleParentChange(index, "emergencyContactName")}
                          />
                        </Field>

                        <Field>
                          <Label htmlFor={`parent-${index}-emergencyContactRelationship`}>Relationship</Label>
                          <Input
                            id={`parent-${index}-emergencyContactRelationship`}
                            type="text"
                            placeholder="e.g. Brother, Friend"
                            value={parent.emergencyContactRelationship}
                            onChange={handleParentChange(index, "emergencyContactRelationship")}
                          />
                        </Field>

                        <Field>
                          <Label htmlFor={`parent-${index}-emergencyContactPhone`}>Phone</Label>
                          <Input
                            id={`parent-${index}-emergencyContactPhone`}
                            type="tel"
                            placeholder="(555) 555-5555"
                            value={parent.emergencyContactPhone}
                            onChange={handleParentChange(index, "emergencyContactPhone")}
                          />
                        </Field>
                      </div>
                    </div>

                    <Divider soft />

                    {/* Attorney Information */}
                    <div>
                      <h4 className="text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-3">
                        Attorney Information
                      </h4>
                      <Field>
                        <Label htmlFor={`parent-${index}-attorneyName`}>Attorney Name</Label>
                        <Input
                          id={`parent-${index}-attorneyName`}
                          type="text"
                          placeholder="Attorney's name"
                          value={parent.attorneyName}
                          onChange={handleParentChange(index, "attorneyName")}
                        />
                      </Field>
                    </div>
                  </div>
                )}
              </div>
            </FieldGroup>
          </Fieldset>
        </div>
      ))}

      {/* Validation message */}
      {!isValid && (
        <p className="text-sm text-amber-600 dark:text-amber-400">
          At least one parent with first and last name is required
        </p>
      )}

      {/* Navigation */}
      <div className="flex justify-between pt-4">
        <Button type="button" plain onClick={onBack}>
          Back
        </Button>
        <Button type="button" onClick={onNext} disabled={!isValid}>
          Continue to Children
        </Button>
      </div>
    </div>
  );
};

export default ParentsStep;
